import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { User } from '@supabase/supabase-js';
import Navbar from './components/Navbar';
import PullToRefresh from './components/PullToRefresh';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import CreateTest from './pages/CreateTest';
import TestView from './pages/TestView';
import TestResults from './pages/TestResults';
import Admin from './pages/Admin';
import { ThemeProvider } from './lib/ThemeContext';
import { motion, AnimatePresence } from 'motion/react';
import About from './pages/About';
import Safety from './pages/Safety';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Profile from './pages/Profile';
import InboxPage from './pages/Inbox';
import { getPendingTest, submitPendingTest } from './lib/pendingTest';
import PhoneNumberPrompt from './components/PhoneNumberPrompt';
import AnalyticsTracker from './components/AnalyticsTracker';
import { captureRefFromUrl, claimPendingReferral } from './lib/referral';
import { recordAgeConfirmation } from './lib/ageConsent';

// Remembers an invite link (?ref=…) and credits the inviter once the new user
// is signed in. The rules are enforced server-side.
function ReferralHandler({ user }: { user: User | null }) {
  const { search } = useLocation();
  useEffect(() => { captureRefFromUrl(); }, [search]);
  useEffect(() => { if (user) claimPendingReferral(user); }, [user?.id]);
  return null;
}

// Google sign-up leaves the site and comes back, so the "I'm 18+" confirmation
// is saved on the account once they're signed in again.
function AgeConsentHandler({ user }: { user: User | null }) {
  useEffect(() => { if (user) recordAgeConfirmation(user); }, [user?.id]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

// Fires the moment a session exists (fresh signup, confirmed email, login,
// or Google OAuth return) and there's a draft test saved from the
// signed-out "Start a Session" flow. Submits it and drops the person on
// their dashboard — no re-entering anything.
function PendingTestAutoSubmit({ user }: { user: User | null }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) return;
    if (!getPendingTest()) return;
    (async () => {
      const id = await submitPendingTest(user);
      if (id) navigate(`/dashboard?newTest=${id}`);
    })();
  }, [user, navigate]);
  return null;
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'instant' });
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleRefresh = useCallback(async () => {
    await new Promise(res => setTimeout(res, 600));
    setRefreshKey(k => k + 1);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-white">
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="text-2xl font-light tracking-widest uppercase"
        >
          Loading...
        </motion.div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <ScrollToTop />
        <AnalyticsTracker user={user} />
        <ReferralHandler user={user} />
        <AgeConsentHandler user={user} />
        <PendingTestAutoSubmit user={user} />
        {user && <PhoneNumberPrompt user={user} />}
        <PullToRefresh onRefresh={handleRefresh}>
          <div className="min-h-screen min-h-dvh bg-zinc-950 text-zinc-100 font-sans selection:bg-rose-500/30">
            <Navbar user={user} />
            <main className="w-full">
              <AnimatePresence mode="wait">
                <Routes>
                  <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Home key={`home-${refreshKey}`} user={user} />} />
                  <Route
                    path="/dashboard"
                    element={user ? <Dashboard key={`dash-${refreshKey}`} user={user} /> : <Navigate to="/" />}
                  />
                  <Route
                    path="/create"
                    element={<CreateTest key={`create-${refreshKey}`} user={user} />}
                  />
                  <Route path="/test/:testId" element={<TestView key={`test-${refreshKey}`} />} />
                  <Route
                    path="/results/:testId"
                    element={user ? <TestResults key={`results-${refreshKey}`} user={user} /> : <Navigate to="/" />}
                  />
                  <Route path="/admin" element={user ? <Admin key={`admin-${refreshKey}`} user={user} /> : <Navigate to="/" />} />
                  <Route path="/profile" element={user ? <Profile user={user} /> : <Navigate to="/" />} />
                  <Route path="/inbox" element={user ? <InboxPage key={`inbox-${refreshKey}`} user={user} /> : <Navigate to="/" />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/safety" element={<Safety />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />

                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </AnimatePresence>
            </main>
          </div>
        </PullToRefresh>
      </Router>
    </ThemeProvider>
  );
}
